import { Seq } from 'immutable';
import { createSelector } from 'reselect';
import { formatDate } from '../../utils/dates/date';

export const getCompetitions = state => state.getIn(['events', 'entities', 'competitions']);
export const getEvents = state => state.getIn(['events', 'entities', 'events']);
const getMarkets = state => state.getIn(['events', 'entities', 'markets']);
export const getCompetitionIds = state => state.getIn(['events', 'competitionIds']);

export const isLoading = state => state.getIn(['events', 'isLoading']);

export const getCompetition = (state, { competitionId }) => (
  state.getIn(['events', 'entities', 'competitions', competitionId])
);
export const getEvent = (state, { eventId }) => (
  state.getIn(['events', 'entities', 'events', eventId])
);
export const getMarket = (state, { marketId }) => (
  state.getIn(['events', 'entities', 'markets', marketId])
);
export const getSelection = (state, { selectionId }) => (
  state.getIn(['events', 'entities', 'selections', selectionId])
);

export const getEventMarketByMarketName = (state, { eventId, marketName }) => (
  getMarkets(state).find(m => m.eventId === eventId && m.marketName === marketName)
);

export const hasEvents = createSelector(
  getEvents,
  (events) => (events ? !!events.size : false)
);

export const isEventExists = createSelector(
  getEvent,
  event => !!event
);

export const getEventByMarketId = createSelector(
  getEvents,
  getMarket,
  (events, market) => events.get(market.eventId)
);

export const getCompetitionByEventId = createSelector(
  getCompetitions,
  getEvent,
  (competitions, event) => event && competitions.get(event.competitionId)
);

export const makeGetCompetitionName = () => createSelector(
  getCompetition,
  competition => competition.competitionName
);

export const makeGetCompetitionEvents = () => createSelector(
  getCompetition,
  competition => competition.events
);

export const makeGetCompetitionMarketGroups = () => createSelector(
  getCompetition,
  getMarkets,
  (competition, markets) => new Seq(markets)
    .filter(market => market.competitionId === competition.competitionId)
    .map(market => market.marketName)
    .sort()
    .toSet()
    .toList()
);

export const makeGetEventName = () => createSelector(
  getEvent,
  event => (event ? event.eventName : '')
);

export const makeGetEventHome = () => createSelector(
  makeGetEventName(),
  eventName => eventName.split(/( vs?\.? )/)[0]
);

export const makeGetEventAway = () => createSelector(
  makeGetEventName(),
  eventName => eventName.split(/( vs?\.? )/).slice(2).join('')
);

export const makeIsEventInPlay = () => createSelector(
  getEvent,
  event => event && event.eventInPlay
);

export const isEventFinished = createSelector(
  getEvent,
  event => event && event.eventIsFinished
);

export const makeGetEventScoreHome = () => createSelector(
  getEvent,
  event => (event ? event.scoreHome : '')
);

export const makeGetEventScoreAway = () => createSelector(
  getEvent,
  event => (event ? event.scoreAway : '')
);

export const getEventDate = createSelector(
  getEvent,
  event => event && formatDate(new Date(event.eventStartDateTime.substr(0, 10)))
);

export const getEventPeriod = createSelector(
  getEvent,
  event => event && event.eventPeriod
);

export const getEventCompetition = createSelector(
  getCompetitionByEventId,
  competition => competition && competition.competitionName
);

export const abbrTime = (time, period) => {
  if (time === '') {
    return '';
  }
  const [min] = time.split(':').map(Number);
  if (min > 90 && period === 'second half') {
    return `90+${min - 90}'`;
  }
  if (min > 45 && period === 'first half') {
    return `45+${min - 45}'`;
  }
  return `${min}'`;
};

export const recordWithAbbrTime = (timeField, periodField) => (
  record => record.set(timeField, abbrTime(record[timeField], record[periodField]))
);

export const getEventTimeAbbr = createSelector(
  getEvent,
  event => (event ? abbrTime(event.eventLiveTime, event.eventPeriod) : '')
);

export const getEventTimeOrPeriod = createSelector(
  getEvent,
  getEventTimeAbbr,
  (event, time) => {
    const periods = [
      'not started',
      'half time',
      'extra time not started',
      'extra time half time',
      'penalties not started',
      'penalties',
      'game finished',
      'total'
    ];
    if (!event) {
      return '';
    }
    if (periods.includes(event.eventPeriod)) {
      return event.eventPeriod;
    }
    return time;
  }
);

export const makeGetEventTimeOrPeriodAbbr = () => createSelector(
  getEvent,
  getEventTimeAbbr,
  (event, time) => {
    const periods = new Map([
      ['half time', 'HT'],
      ['extra time not started', 'ET'],
      ['extra time half time', 'ET HT'],
      ['penalties not started', 'PEN'],
      ['penalties', 'PEN'],
      ['game finished', 'FT'],
      ['total', 'FT']
    ]);
    if (!event) {
      return '';
    }
    if (periods.has(event.eventPeriod)) {
      return periods.get(event.eventPeriod);
    }
    return time;
  }
);

export const countGoalsPerPeriod = (acc, g) => acc.set(g.period, (acc.get(g.period) || 0) + 1);

export const getEventHalfTimeResult = createSelector(
  getEvent,
  event => {
    const periods = [
      'not started',
      'first half',
      'half time'
    ];
    if (!event || !event.goalsHome || !event.goalsAway || periods.includes(event.eventPeriod)) {
      return null;
    }
    const home = event.goalsHome.reduce(countGoalsPerPeriod, new Map());
    const away = event.goalsAway.reduce(countGoalsPerPeriod, new Map());
    return {
      home: home.get('first half') || 0,
      away: away.get('first half') || 0
    };
  }
);

export const getEventGoals = createSelector(
  getEvent,
  event => event && event.goalsHome && event.goalsAway && {
    home: event.goalsHome,
    away: event.goalsAway
  }
);

export const getEventGoalsAbbr = createSelector(
  getEvent,
  event => event && event.goalsHome && event.goalsAway && {
    home: event.goalsHome.map(recordWithAbbrTime('time', 'period')),
    away: event.goalsAway.map(recordWithAbbrTime('time', 'period'))
  }
);

export const getEventCards = createSelector(
  getEvent,
  event => event && event.cardsHome && event.cardsAway && {
    home: event.cardsHome,
    away: event.cardsAway
  }
);

export const getEventCardsAbbr = createSelector(
  getEvent,
  event => event && event.cardsHome && event.cardsAway && {
    home: event.cardsHome.map(recordWithAbbrTime('time', 'period')),
    away: event.cardsAway.map(recordWithAbbrTime('time', 'period'))
  }
);

export const getTotalStats = createSelector(
  getEvent,
  event => {
    if (!event || !event.totalStats) {
      return undefined;
    }
    const stats = event.totalStats;
    const shotsTotal = {
      home: stats.shotsOnTarget.home + stats.shotsOffTarget.home + stats.shotsOnWoodwork.home,
      away: stats.shotsOnTarget.away + stats.shotsOffTarget.away + stats.shotsOnWoodwork.away
    };
    const percent = value => Math.round(value * 100);
    return {
      shotsOnTarget: stats.shotsOnTarget,
      shotsOnWoodwork: stats.shotsOnWoodwork,
      corners: stats.corners,
      goalKicks: stats.goalKicks,
      shotsOffTarget: stats.shotsOffTarget,
      attacks: stats.attacks,
      dangerousAttacks: stats.dangerousAttacks,
      freeKicks: stats.freeKicks,
      throwIns: stats.throwIns,
      possession: stats.possession,
      penalties: stats.penalties,
      redCards: stats.redCards,
      yellowCards: stats.yellowCards,
      possessionTime: stats.possessionTime,
      possessionPercent: {
        home: percent(stats.possession.home),
        away: percent(stats.possession.away)
      },
      shotsTotal,
      shotAccuracy: {
        home: shotsTotal.home ? percent(stats.shotsOnTarget.home / shotsTotal.home) : 0,
        away: shotsTotal.away ? percent(stats.shotsOnTarget.away / shotsTotal.away) : 0
      },
      totalCards: {
        home: stats.redCards.home + stats.yellowCards.home,
        away: stats.redCards.away + stats.yellowCards.away
      }
    };
  }
);

export const makeGetEventSelectionsByMarketName = () => createSelector(
  getEventMarketByMarketName,
  market => market && market.selections
);

export const makeGetEventStartTime = () => createSelector(
  getEvent,
  event => (event ? event.eventStartTime : '')
);

export const getSelectionPrice = createSelector(
  getSelection,
  selection => `${selection.selectionPriceNum}/${selection.selectionPriceDen}`
);

export const getMarketBySelectionId = createSelector(
  getMarkets,
  getSelection,
  (markets, selection) => markets.get(selection.marketId)
);

export const getEventBySelectionId = createSelector(
  getEvents,
  getSelection,
  (events, selection) => events.get(selection.eventId)
);

export const makeGetFullSelectionData = () => createSelector(
  getSelection,
  getMarketBySelectionId,
  getEventBySelectionId,
  (selection, market, event) => ({
    selectionId: selection.selectionId,
    selectionName: selection.selectionName,
    selectionPriceNum: selection.selectionPriceNum,
    selectionPriceDen: selection.selectionPriceDen,
    marketId: market.marketId,
    marketName: market.marketName,
    eventId: event.eventId,
    eventName: event.eventName,
    eventStartDateTime: event.eventStartDateTime
  })
);
